/* eslint-disable @typescript-eslint/no-explicit-any */
import { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { Toast } from 'sharedUi/Toast';
import { Tag } from 'primereact/tag';
import { Button } from 'sharedUi/Button';
import { RefreshButton } from 'sharedUi/RefreshButton';
import { useSecureNavigation } from '../../hooks/useSecureNavigation';
import { functionsService, type FunctionMaster } from '../../services/functions.service';
import { extractErrorMessage } from '../../utils/error-handler';
import PermissionGuard from '../../components/guards/PermissionGuard';
import { JOB_NAMES } from '../../constants/jobNames';
import PageHeader from '../../components/common/page-header';
import SaasDataTable, { type SaasColumn } from '../../components/common/saas-data-table';
import { ProjectCell } from '../../components/common/project-cell';
import { useAccessibleProjects } from '../../hooks/useProjectScope';
import DeleteConfirmDialog from '../../components/dialogs/delete-confirm-dialog';
import { formatTimestamp } from 'sharedUi/dateUtils';
import { useCanCreate, useCanUpdate, useCanDelete } from '../../hooks/usePermissions';

const FunctionList = () => {
  const { navigateToCreate, navigateToView, navigateToEdit } = useSecureNavigation({
    entity: 'function', basePath: '/functions',
  });
  const toast = useRef<Toast>(null);
  const canCreate = useCanCreate(JOB_NAMES.FUNCTION_MASTER);
  const canUpdate = useCanUpdate(JOB_NAMES.FUNCTION_MASTER);
  const canDelete = useCanDelete(JOB_NAMES.FUNCTION_MASTER);

  // Function master is the one master here whose appId the API takes from the
  // caller, so this column reflects a real choice rather than the token.
  const { projects, canScopeByProject } = useAccessibleProjects();

  const [rows, setRows] = useState<FunctionMaster[]>([]);
  const [loading, setLoading] = useState(true);
  const [first, setFirst] = useState(0);
  const [pageSize, setPageSize] = useState(10);
  const [total, setTotal] = useState(0);
  // Server-side, newest first until the viewer picks a column.
  const [sort, setSort] = useState<{ field: string | null; order: 1 | -1 | null }>({ field: 'createdAt', order: -1 });
  const [toDelete, setToDelete] = useState<FunctionMaster | null>(null);

  const showError = useCallback((m: string) => toast.current?.show({ severity: 'error', summary: 'Error', detail: m, life: 8000 }), []);

  const fetchRows = useCallback(async (page = 1, size = pageSize, sorting = sort) => {
    try {
      setLoading(true);
      const resp = await functionsService.list({
        page, page_size: size,
        sort_by: sorting.field || undefined,
        sort_order: sorting.order === 1 ? 'asc' : sorting.order === -1 ? 'desc' : undefined,
      });
      setRows(resp.data || []);
      setTotal(resp.pagination?.totalItems ?? resp.pagination?.total ?? (resp.data || []).length);
    } catch (e) {
      showError(extractErrorMessage(e, 'Failed to load functions'));
    } finally {
      setLoading(false);
    }
  }, [pageSize, sort, showError]);

  useEffect(() => { fetchRows(1, pageSize); /* eslint-disable-next-line */ }, []);

  const handlePage = (e: any) => { setFirst(e.first); setPageSize(e.rows); fetchRows(Math.floor(e.first / e.rows) + 1, e.rows); };

  const handleSort = (e: any) => {
    const next = { field: e.sortField || null, order: e.sortOrder || null };
    setSort(next);
    setFirst(0);
    fetchRows(1, pageSize, next);
  };

  const handleDelete = async () => {
    if (!toDelete) return;
    try {
      await functionsService.softDelete(toDelete._id);
      setToDelete(null);
      toast.current?.show({ severity: 'success', summary: 'Deleted', detail: 'Function deleted', life: 3000 });
      fetchRows(Math.floor(first / pageSize) + 1, pageSize);
    } catch (e) {
      showError(extractErrorMessage(e, 'Failed to delete function'));
    }
  };

  const columns: SaasColumn<FunctionMaster>[] = useMemo(() => [
    { field: 'functionCode', header: 'Code', sortable: true, body: (r) => <span className="font-mono text-xs">{r.functionCode}</span> },
    { field: 'capability', header: 'Capability', sortable: true, body: (r) => <Tag value={r.capability} severity={r.capability === 'EVAL_TARGET' ? 'info' : 'secondary'} className="text-xs px-1.5 py-0.5" /> },
    { field: 'functionType', header: 'Type', sortable: true, body: (r) => <span className="text-xs">{r.functionType}</span> },
    { field: 'description', header: 'Description', body: (r) => <span className="text-sm">{r.description || '-'}</span> },
    {
      field: 'appId', header: 'Project', style: { minWidth: 150 },
      body: (r: FunctionMaster) => <ProjectCell appId={r.appId} projects={projects} />,
    },
    { field: 'createdAt', header: 'Created', sortable: true, body: (r) => <span className="text-xs text-gray-500 whitespace-nowrap">{r.createdAt ? formatTimestamp(r.createdAt) : '-'}</span> },
    {
      field: 'actions', header: 'Actions', align: 'center', hideable: false,
      body: (r) => (
        <div className="flex gap-1 justify-center">
          <button aria-label="View" className="p-2 rounded hover:bg-gray-100 dark:hover:bg-gray-700" onClick={() => navigateToView(r._id)} title="View">
            <i className="pi pi-eye text-green-600 dark:text-green-400" />
          </button>
          {canUpdate && <button aria-label="Edit" className="p-2 rounded hover:bg-gray-100 dark:hover:bg-gray-700" onClick={() => navigateToEdit(r._id)} title="Edit">
            <i className="pi pi-pencil text-blue-600 dark:text-blue-400" />
          </button>}
          {canDelete && <button aria-label="Delete" className="p-2 rounded hover:bg-gray-100 dark:hover:bg-gray-700" onClick={() => setToDelete(r)} title="Delete">
            <i className="pi pi-trash text-red-600 dark:text-red-400" />
          </button>}
        </div>
      ),
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
  ], [canUpdate, canDelete, canScopeByProject, projects]);

  return (
    <PermissionGuard jobName={JOB_NAMES.FUNCTION_MASTER}>
      <Toast ref={toast} position="top-right" />
      <div className="p-6" data-testid="SCR-Function-List">
        <PageHeader icon="pi pi-bolt" title="Functions" subtitle="Reusable resolution capabilities (recipient resolution + eval targets)" testId="Function.Header" />
        <div className="ds-card overflow-hidden">
          <SaasDataTable
            tableId="function-master" columns={columns} data={rows} loading={loading} dataKey="_id"
            emptyMessage="No functions found" first={first} rows={pageSize} totalRecords={total}
            onPage={handlePage} rowsPerPageOptions={[5, 10, 25, 50]} sortField={sort.field || undefined} sortOrder={sort.order} onSort={handleSort} onFilter={() => {}} testId="FunctionList"
            toolbarEnd={<>
              <span className="text-sm text-gray-400 whitespace-nowrap">{total} {total === 1 ? 'record' : 'records'}</span>
              <RefreshButton onClick={() => fetchRows(Math.floor(first / pageSize) + 1, pageSize)} loading={loading} testId="FunctionList.Refresh" />
              {canCreate && <Button label="Create Function" icon="pi pi-plus" onClick={() => navigateToCreate()} size="small" />}
            </>}
          />
        </div>
      </div>
      <DeleteConfirmDialog visible={!!toDelete} onCancel={() => setToDelete(null)} onDelete={handleDelete}
        message={`Delete function '${toDelete?.functionCode}'?`} testId="FunctionList.Delete" />
    </PermissionGuard>
  );
};

export default FunctionList;
